import { Dropdown, Modal, Button, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import ReportsView from "../components/reports/ReportsView";
import { Guid } from "guid-typescript";
import Loader from "../controls/Loader";
import { SearchQuery } from "../code/search-query/search-query";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getWellbeingReportExport } from "../repository/data.repository";
import { WellbeingReport } from "../repository/data.default";
import { ReportForm } from "../components/reports/ReportsFormDialog.default";
import ReportsFormDialog from "../components/reports/ReportsFormDialog";
import ReportManageDialog from "../components/reports/ReportManageDialog";
import { getProblemMessage } from "../utils/http";

function ReportsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [reportInputMode, setReportInputMode] = useState<
    "add" | "delete" | "manage"
  >("add");
  const [reportInputOpen, setReportInputOpen] = useState(false);
  const [reportInputValues, setReportInputValues] = useState<ReportForm>(
    {} as any,
  );
  const [manageRecord, setManageRecord] = useState<WellbeingReport | undefined>(
    undefined,
  );
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onReportAction(
    mode: "add" | "delete" | "manage",
    values?: WellbeingReport,
  ): void {
    setReportInputMode(mode);
    setManageRecord(mode === "manage" ? values : undefined);
    setReportInputValues(
      values
        ? {
            uid: values.uid,
            rowVersion: values.rowVersion,
            company: { display: values.companyName, value: values.companyUid },
            user: {
              display: values.reportingForUserUserName,
              value: values.reportingForUserUid,
            },
            summary: values.summary,
          }
        : ({} as any),
    );
    setReportInputOpen(true);
  }

  function onReportInputFinish(): void {
    setReportInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onReportInputClose(): void {
    setReportInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading Wellbeing Reports export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getWellbeingReportExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Wellbeing Reports
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("WellbeingReports", "create")}

            className={`${!permissions.can("WellbeingReports", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("WellbeingReports", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onReportAction("add")}
          >
            Add Report
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <ReportsView
          trigger={triggerView}
          onAction={(key: string, record: any) =>
            onReportAction(key as any, record)
          }
          onSearch={onSearch}
        />
      </Col>
      <ReportsFormDialog
        mode={reportInputMode === "manage" ? "add" : reportInputMode}
        open={reportInputOpen && reportInputMode !== "manage"}
        initialValues={reportInputValues}
        onFinish={onReportInputFinish}
        onCancel={onReportInputClose}
      />
      <ReportManageDialog
        open={reportInputOpen && reportInputMode === "manage"}
        record={manageRecord}
        onFinish={onReportInputFinish}
        onCancel={onReportInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default ReportsPage;
